import { useId, useRef, useState } from "react";

import {
  classifyAccessFailure,
  useReplaceSubjectRolesMutation,
  type DirectoryUser,
  type Role,
  type RoleCatalog,
} from "@/api/access";
import { QueryErrorState } from "@/api/QueryErrorState";
import {
  ROLE_LABEL,
  accessSummary,
  canonicalRoles,
  identityName,
  type SubjectAccessRow,
  type SubjectIdentity,
} from "@/lib/editorAccess";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError } from "@/ui/FieldError";
import { LoadingState } from "@/ui/states";
import { useToast } from "@/ui/Toast";

import { DirectoryPicker } from "./DirectoryPicker";
import { RoleSelector } from "./RoleSelector";

export const SELF_ADMIN_LOCK_REASON =
  "You cannot remove your own Administrator role. Ask another administrator to change it.";

/** The sentence every success toast ends with — the ratified effect timing. */
export const EFFECT_TIMING = "It takes effect on their next request.";

/**
 * Grant access, or edit the access a person already has. ONE dialog, because
 * the two flows share their whole second half — the role selector, the
 * effective-access summary and the atomic write — and differ only in whether
 * the person is picked from the directory or handed in.
 *
 * Every save is `PUT /v1/subject-roles` with the COMPLETE intended set and the
 * live assignment ids the table showed, so a grant, an edit and a no-op are
 * one request each and never a sequence the browser has to pretend is atomic.
 * A person picked in grant mode who already holds access is edited rather
 * than re-granted: their current roles are preselected and their live ids are
 * sent, which is also what turns an "idempotent grant" into a truthful
 * "no changes were needed".
 */
export function GrantAccessDialog({
  mode,
  row,
  identity,
  rows,
  catalog,
  catalogError,
  onCatalogRetry,
  self,
  onClose,
}: {
  mode: "grant" | "edit";
  /** The row being edited. Required in edit mode. */
  row?: SubjectAccessRow;
  /** What the table knows about the edited person. */
  identity?: SubjectIdentity;
  /** Every live row, so a picked person who already has access is edited. */
  rows: readonly SubjectAccessRow[];
  catalog: RoleCatalog | undefined;
  catalogError: unknown;
  onCatalogRetry: () => void;
  self: { subject: string; displayName: string };
  onClose: () => void;
}) {
  const toast = useToast();
  const mutation = useReplaceSubjectRolesMutation({ selfSubject: self.subject });
  const rolesErrorId = useId();
  const summaryId = useId();
  const inFlight = useRef(false);

  const [picked, setPicked] = useState<DirectoryUser | null>(null);
  const [pickedRow, setPickedRow] = useState<SubjectAccessRow | undefined>();
  const target: SubjectAccessRow | undefined =
    mode === "edit"
      ? row
      : pickedRow;
  const subject = mode === "edit" ? row?.subject : picked?.subject;
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(
    mode === "edit" ? (row?.roles ?? []) : [],
  );
  // Which person the current selection belongs to. Picking somebody else in
  // grant mode restarts the selection from THEIR live roles.
  const [selectionFor, setSelectionFor] = useState<string | undefined>(subject);
  if (subject !== selectionFor) {
    setSelectionFor(subject);
    setSelectedRoles(target?.roles ?? []);
  }
  const [showValidation, setShowValidation] = useState(false);

  const displayName =
    mode === "edit"
      ? identity
        ? identityName(identity)
        : (row?.subject ?? "")
      : (picked?.displayName ?? "");
  const email = mode === "edit" ? (identity?.email ?? null) : (picked?.email ?? null);

  const isSelf = subject !== undefined && subject === self.subject;
  const selfHoldsAdmin = isSelf && (target?.roles.includes("admin") ?? false);
  const locked: Partial<Record<Role, string>> = selfHoldsAdmin
    ? { admin: SELF_ADMIN_LOCK_REASON }
    : {};

  const currentRoles = target?.roles ?? [];
  const unchanged =
    subject !== undefined &&
    canonicalRoles(selectedRoles).join(",") ===
      canonicalRoles(currentRoles).join(",");
  const noRoles = selectedRoles.length === 0;
  const busy = mutation.isPending;

  const failure = mutation.isError ? classifyAccessFailure(mutation.error) : null;
  const mustReread = failure?.kind === "stale" || failure?.kind === "ambiguous";

  let alert: string | null = null;
  let mono: string | null = null;
  if (failure) {
    switch (failure.kind) {
      case "stale":
        alert =
          "This person's access was changed by someone else since it was read. The table has been refreshed — close this dialog, review their current access, and decide again.";
        break;
      case "self-admin":
        alert = failure.message;
        break;
      case "redundant":
        alert = failure.message;
        break;
      case "denied":
        alert = "Your account is no longer authorized to manage Editor access.";
        break;
      case "unauthenticated":
        alert = "Your session has expired. Sign in again to continue.";
        break;
      case "directory-unavailable":
        alert = "The Entra directory is unavailable in this deployment.";
        break;
      case "unavailable":
        alert = "Access administration is not available in this environment.";
        break;
      case "ambiguous":
        alert =
          "The Editor Service's answer was interrupted, so the result is unknown. The table has been refreshed — close this dialog and check their access before deciding again.";
        mono = `${failure.code}${failure.requestId ? ` · ${failure.requestId}` : ""}`;
        break;
      case "error":
        alert = `The Editor Service did not accept this change. Nothing was changed. ${failure.message}`;
        mono = `${failure.code}${failure.requestId ? ` · ${failure.requestId}` : ""}`;
        break;
    }
  }

  async function submit() {
    if (subject === undefined || inFlight.current || busy || mustReread || catalogError) {
      return;
    }
    if (noRoles || unchanged) {
      setShowValidation(true);
      return;
    }
    inFlight.current = true;
    try {
      const result = await mutation.mutateAsync({
        subject,
        roles: canonicalRoles(selectedRoles),
        expectedAssignmentIds: (target?.assignments ?? []).map(
          (assignment) => assignment.assignmentId,
        ),
      });
      const changed = result.granted.length > 0 || result.revoked.length > 0;
      const name = displayName || subject;
      if (!changed) {
        toast(`No changes were needed: ${name} already had this access.`);
      } else if (mode === "grant" && !target) {
        toast(`Access granted to ${name}. ${EFFECT_TIMING}`);
      } else {
        toast(`Access updated for ${name}. ${EFFECT_TIMING}`);
      }
      onClose();
    } catch {
      // Rendered from mutation.error above; the dialog stays open so the
      // refusal is read beside the decision that provoked it.
    } finally {
      inFlight.current = false;
    }
  }

  const validation = showValidation
    ? noRoles
      ? mode === "edit"
        ? "Select at least one role, or use Remove Editor access to revoke everything."
        : "Select at least one role."
      : unchanged
        ? "No changes to save: these are the roles this person already holds."
        : null
    : null;

  const submitLabel =
    mode === "edit" || target ? "Save changes" : "Grant access";
  const canSubmit =
    subject !== undefined && catalog !== undefined && !catalogError && !busy && !mustReread;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={mode === "edit" ? `Edit access for ${displayName}` : "Grant Editor access"}
        description={
          mode === "edit"
            ? "Change the roles this person holds in the Clinical Editor. Their Entra account is not changed."
            : "Select an existing person from your organization's Entra directory, then choose the roles they need. This grants Editor access; it does not create an account."
        }
        busy={busy}
        className="w-[min(40rem,calc(100vw-2rem))]"
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={!canSubmit}
              title={
                subject === undefined ? "Choose a person first." : undefined
              }
            >
              {busy ? "Saving…" : submitLabel}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {mode === "grant" ? (
            <DirectoryPicker
              selected={picked}
              onSelect={(person) => {
                setPicked(person);
                setPickedRow(rows.find((candidate) => candidate.subject === person.subject));
              }}
              disabled={busy}
              assignedRoles={(candidate) =>
                rows.find((item) => item.subject === candidate)?.roles ?? null
              }
            />
          ) : null}

          {subject !== undefined ? (
            <section
              aria-label="Selected person"
              className="rounded-md border border-border bg-muted/40 p-3"
            >
              <p className="text-sm font-medium text-foreground">
                {displayName || "Unknown Entra user"}
                {isSelf ? (
                  <Badge tone="outline" className="ml-2">
                    You
                  </Badge>
                ) : null}
              </p>
              {email ? (
                <p className="text-xs text-muted-foreground">{email}</p>
              ) : null}
              <p className="mt-1 text-xs text-muted-foreground">
                Entra Object ID:{" "}
                <span className="select-all font-mono text-foreground">
                  {subject}
                </span>
              </p>
              {mode === "grant" && target ? (
                <p className="mt-2 text-xs text-foreground">
                  This person already has Editor access (
                  {target.roles.map((role) => ROLE_LABEL[role]).join(", ")}).
                  Saving replaces their current roles.
                </p>
              ) : null}
            </section>
          ) : null}

          {catalogError ? (
            <QueryErrorState
              error={catalogError}
              onRetry={onCatalogRetry}
              title="Editor roles could not be loaded"
              description="Reload the role catalog before choosing access."
              subject="Editor roles"
            />
          ) : null}
          {subject !== undefined && catalog === undefined && !catalogError ? (
            // The role editor waits for the server's catalog: the inclusion
            // rule that clears a redundant role and the effective-access
            // summary are both read off it, and a selection made before it
            // arrived would be one the service refuses.
            <LoadingState label="Loading the role catalog" rows={2} />
          ) : null}
          {subject !== undefined && catalog !== undefined && !catalogError ? (
            <>
              <RoleSelector
                selected={selectedRoles}
                onChange={(next) => {
                  setSelectedRoles(next);
                  setShowValidation(false);
                }}
                catalog={catalog}
                disabled={busy || mustReread}
                locked={locked}
                {...(validation ? { describedBy: rolesErrorId } : {})}
              />
              {validation ? (
                <FieldError id={rolesErrorId} message={validation} />
              ) : null}
              <p
                id={summaryId}
                role="status"
                aria-live="polite"
                data-testid="effective-access"
                className="rounded-md bg-accent/40 px-3 py-2 text-sm text-foreground"
              >
                <span className="font-medium">Effective access: </span>
                {accessSummary(selectedRoles, catalog)}
              </p>
            </>
          ) : null}

          {alert ? (
            <p role="alert" className="text-sm text-destructive">
              {alert}
            </p>
          ) : null}
          {mono ? (
            <p className="font-mono text-xs text-muted-foreground">{mono}</p>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Changes take effect on the person's next authenticated request; no
            sign-out is needed. Every grant and revocation is retained for audit.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
